/*
 * Copyright 2026 Sony Corporation
 */

import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Switch,
  Typography,
} from '@mui/material';
import { APERTURE_STOPS, LENSES, type Optics } from '@ips-device-verse/twin-service/schema';

import {
  aspectLabel,
  depthOfField,
  fieldOfView,
  footprint,
  pixelPitch,
  type ResolvedOptics,
} from '../../scene/camera-optics';
import { NumberField } from './NumberField';

export interface OpticsPanelProps {
  optics: ResolvedOptics;
  onChange: (change: Partial<Optics>) => void;
  pending?: boolean;
  error?: string;
}

const DEGREES = 180 / Math.PI;

const Readout = ({ label, value }: { label: string; value: string }) => (
  <Box className="flex items-baseline justify-between gap-2">
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" className="font-mono">
      {value}
    </Typography>
  </Box>
);

const Status = ({ pending, error }: { pending?: boolean; error?: string }) => (
  <>
    {pending && (
      <Typography variant="caption" color="warning.main">
        saving…
      </Typography>
    )}
    {error && (
      <Typography variant="caption" color="error">
        {error}
      </Typography>
    )}
  </>
);

const metres = (value: number): string => (Number.isFinite(value) ? `${value.toFixed(2)} m` : '∞');

export interface LensPanelProps extends OpticsPanelProps {
  depthOfFieldShown: boolean;
  onDepthOfFieldShownChange: (shown: boolean) => void;
}

/** Lens, focal length (the zoom), aperture and focus, and what they add up to. */
export const LensPanel = ({
  optics,
  onChange,
  pending,
  error,
  depthOfFieldShown,
  onDepthOfFieldShownChange,
}: LensPanelProps) => {
  const { lens } = optics;
  const { focal_length_mm: focal, aperture_f: aperture, focus_distance_m: focus } = optics.optics;
  const prime = lens.focal_mm.min === lens.focal_mm.max;
  const stops = APERTURE_STOPS.filter((stop) => stop >= lens.aperture.widest && stop <= lens.aperture.narrowest);
  const fov = fieldOfView(optics);
  const dof = depthOfField(optics);

  return (
    <Box className="flex flex-col gap-3 pt-2">
      <FormControl size="small" fullWidth>
        <InputLabel id="optics-lens">Lens</InputLabel>
        <Select
          labelId="optics-lens"
          label="Lens"
          value={lens.id}
          onChange={(event) => onChange({ lens_id: event.target.value })}
        >
          {LENSES.map((candidate) => (
            <MenuItem key={candidate.id} value={candidate.id}>
              {candidate.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box>
        <Box className="flex items-baseline justify-between gap-2">
          <Typography variant="body2" color="text.secondary">
            Focal length
          </Typography>
          <Typography variant="body2" className="font-mono">
            {focal.toFixed(0)} mm{prime ? '' : ` · ${(focal / lens.focal_mm.min).toFixed(1)}×`}
          </Typography>
        </Box>
        <Slider
          size="small"
          aria-label="Focal length"
          disabled={prime}
          min={lens.focal_mm.min}
          max={lens.focal_mm.max}
          step={1}
          value={focal}
          onChange={(_event, value) => onChange({ focal_length_mm: value })}
        />
      </Box>

      <Box className="grid grid-cols-2 gap-2">
        <FormControl size="small" fullWidth>
          <InputLabel id="optics-aperture">Aperture</InputLabel>
          <Select
            labelId="optics-aperture"
            label="Aperture"
            value={aperture}
            onChange={(event) => onChange({ aperture_f: Number(event.target.value) })}
          >
            {/* A stored value between stops stays selectable rather than silently snapping. */}
            {(stops.includes(aperture) ? stops : [...stops, aperture].sort((a, b) => a - b)).map((stop) => (
              <MenuItem key={stop} value={stop}>
                F{stop}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <NumberField
          label="Focus"
          unit="m"
          value={focus}
          min={lens.min_focus_m}
          max={10_000}
          onCommit={(value) => onChange({ focus_distance_m: value })}
        />
      </Box>

      <Box className="flex flex-col gap-1">
        <Readout
          label="Field of view"
          value={`${(fov.horizontal * DEGREES).toFixed(1)}° × ${(fov.vertical * DEGREES).toFixed(1)}°`}
        />
        <Readout label="Diagonal" value={`${(fov.diagonal * DEGREES).toFixed(1)}°`} />
        <Readout label="Sharp from" value={`${metres(dof.near)} to ${metres(dof.far)}`} />
        <Readout label="Hyperfocal" value={metres(dof.hyperfocal)} />
      </Box>

      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={depthOfFieldShown}
            onChange={(event) => onDepthOfFieldShownChange(event.target.checked)}
          />
        }
        label={<Typography variant="body2">Depth of field in preview</Typography>}
      />

      <Status pending={pending} error={error} />
    </Box>
  );
};

/** The body's sensor, the recording format, and the resolution that buys on the subject. */
export const SensorPanel = ({ optics, onChange, pending, error }: OpticsPanelProps) => {
  const { body, format, sensor, knownBody } = optics;
  const focus = optics.optics.focus_distance_m;
  const covered = footprint(optics, focus);

  return (
    <Box className="flex flex-col gap-3 pt-2">
      {!knownBody && (
        <Typography variant="caption" color="warning.main">
          This model is not in the catalog, so it is simulated as a generic full-frame body.
        </Typography>
      )}
      <Readout label="Sensor" value={`${body.sensor_mm.width} × ${body.sensor_mm.height} mm`} />

      <FormControl size="small" fullWidth>
        <InputLabel id="optics-format">Format</InputLabel>
        <Select
          labelId="optics-format"
          label="Format"
          value={format.id}
          onChange={(event) => onChange({ format_id: event.target.value })}
        >
          {body.formats.map((candidate) => (
            <MenuItem key={candidate.id} value={candidate.id}>
              {candidate.label} · {candidate.width_px} × {candidate.height_px}
              {candidate.crop ? ` · ${candidate.crop}× crop` : ''}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box className="flex flex-col gap-1">
        <Readout label="Aspect" value={aspectLabel(format.width_px, format.height_px)} />
        <Readout label="Recorded area" value={`${sensor.width.toFixed(1)} × ${sensor.height.toFixed(1)} mm`} />
        <Readout
          label={`Covers at ${focus.toFixed(1)} m`}
          value={`${covered.width.toFixed(2)} × ${covered.height.toFixed(2)} m`}
        />
        <Readout label="Detail at focus" value={`${pixelPitch(optics, focus).toFixed(2)} mm/px`} />
      </Box>

      <Status pending={pending} error={error} />
    </Box>
  );
};
